import type { Locale, LocalizedText } from '../api/types';

const intlLocale = (locale: Locale) => (locale === 'zh' ? 'zh-CN' : 'en-US');

/** Picks the right side of a bilingual field, falling back to the other one. */
export function pickText(text: LocalizedText | null | undefined, locale: Locale): string {
  if (!text) return '';
  const preferred = locale === 'zh' ? text.zh : text.en;
  return preferred || (locale === 'zh' ? text.en : text.zh) || '';
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(intlLocale(locale)).format(value);
}

/** Compact form for hero statistics: 12.5K / 1.2万. */
export function formatCompact(value: number, locale: Locale): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

export function formatSigned(value: number, locale: Locale): string {
  const formatted = formatNumber(Math.abs(value), locale);
  return value < 0 ? `−${formatted}` : `+${formatted}`;
}

/**
 * Speedrun times are the most-read numbers on the site, so they get a fixed
 * shape: `h:mm:ss.mmm`, with the hour dropped when it is zero.
 */
export function formatDuration(durationMs: number, options?: { millis?: boolean }): string {
  const showMillis = options?.millis ?? true;
  const total = Math.max(0, Math.round(durationMs));
  const hours = Math.floor(total / 3_600_000);
  const minutes = Math.floor((total % 3_600_000) / 60_000);
  const seconds = Math.floor((total % 60_000) / 1000);
  const millis = total % 1000;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  const base = hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
  return showMillis ? `${base}.${String(millis).padStart(3, '0')}` : base;
}

/** Human threshold label, e.g. “30:00” for a tier cap. */
export function formatTierCap(durationMs: number): string {
  return formatDuration(durationMs, { millis: false });
}

export function parseDurationInput(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  // Accepts h:mm:ss.mmm, mm:ss.mmm, or plain seconds.
  const match = /^(?:(\d{1,2}):)?(?:(\d{1,2}):)?(\d{1,2})(?:[.,](\d{1,3}))?$/.exec(trimmed);
  if (!match) return null;
  const [, a, b, c, fraction] = match;
  const parts = [a, b, c].filter((part): part is string => part !== undefined);
  let hours = 0;
  let minutes = 0;
  let seconds = 0;
  if (parts.length === 3) [hours, minutes, seconds] = parts.map(Number);
  else if (parts.length === 2) [minutes, seconds] = parts.map(Number);
  else [seconds] = parts.map(Number);
  if (minutes > 59 || seconds > 59) return null;
  const millis = fraction ? Number(fraction.padEnd(3, '0')) : 0;
  return hours * 3_600_000 + minutes * 60_000 + seconds * 1000 + millis;
}

export function formatDate(value: string | null | undefined, locale: Locale): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(intlLocale(locale), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function formatDateTime(value: string | null | undefined, locale: Locale): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(intlLocale(locale), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatRelative(value: string | null | undefined, locale: Locale): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const absolute = Math.abs(diffSeconds);
  const relative = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: 'auto' });
  if (absolute < 60) return relative.format(Math.round(diffSeconds), 'second');
  if (absolute < 3600) return relative.format(Math.round(diffSeconds / 60), 'minute');
  if (absolute < 86_400) return relative.format(Math.round(diffSeconds / 3600), 'hour');
  if (absolute < 2_592_000) return relative.format(Math.round(diffSeconds / 86_400), 'day');
  if (absolute < 31_536_000) return relative.format(Math.round(diffSeconds / 2_592_000), 'month');
  return relative.format(Math.round(diffSeconds / 31_536_000), 'year');
}

export function formatPercent(value: number, locale: Locale, digits = 0): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'percent',
    maximumFractionDigits: digits,
  }).format(value);
}

/** Taiwan is not a separate country. TW / TWN are China. */
export function canonicalCountryCode(countryCode: string | null | undefined): string | null {
  if (typeof countryCode !== 'string') return null;
  const upper = countryCode.trim().toUpperCase();
  if (upper === '') return null;
  if (upper === 'TW' || upper === 'TWN' || upper === 'HK' || upper === 'HKG' || upper === 'MO' || upper === 'MAC') {
    return 'CN';
  }
  return upper;
}

/** Flag emoji from an ISO 3166-1 alpha-2 code, with a graceful empty result. */
export function countryFlag(countryCode: string | null | undefined): string {
  const upper = canonicalCountryCode(countryCode);
  if (!upper || upper.length !== 2) return '';
  const base = 0x1f1e6;
  return String.fromCodePoint(base + (upper.charCodeAt(0) - 65), base + (upper.charCodeAt(1) - 65));
}

const COUNTRY_NAMES = new Map<string, Intl.DisplayNames>();

export function countryName(countryCode: string | null | undefined, locale: Locale): string {
  if (!countryCode) return '';
  const key = intlLocale(locale);
  let display = COUNTRY_NAMES.get(key);
  if (!display) {
    display = new Intl.DisplayNames([key], { type: 'region' });
    COUNTRY_NAMES.set(key, display);
  }
  try {
    const code = canonicalCountryCode(countryCode) ?? countryCode;
    return display.of(code) ?? code;
  } catch {
    return countryCode;
  }
}

export function initialsOf(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  // CJK names read better as their first character than as initials.
  if (/[\u3000-\u9fff\uff00-\uffef]/.test(trimmed[0])) return trimmed[0];
  const words = trimmed.split(/\s+/);
  return words.length > 1 ? `${words[0][0]}${words[1][0]}`.toUpperCase() : trimmed.slice(0, 2).toUpperCase();
}
